import { useState, useCallback, useMemo, useEffect } from "react";
import { useAtomValue } from "jotai";
import { RuleException, RuleExceptionFilter } from "./types";
import { getRuleExceptions, createRuleException } from "@/api";
import { SelectedReportingPeriodAtom } from "@/store/ReportingPeriodStateAtom";

export const useExceptionsController = () => {
  const selectedReportingPeriod = useAtomValue(SelectedReportingPeriodAtom);
  const [ruleExceptions, setRuleExceptions] = useState<RuleException[]>([]);
  const [selectedRuleExceptionId, setSelectedRuleExceptionId] = useState<number | null>(null);
  const [filters, setFilters] = useState<RuleExceptionFilter>({
    status: undefined,
    assignedTo: undefined,
    search: undefined,
  });
  const [loading, setLoading] = useState(false);

  // Load rule exceptions when reporting period changes
  useEffect(() => {
    const loadRuleExceptions = async () => {
      if (!selectedReportingPeriod?.reportingPeriodId) {
        setRuleExceptions([]);
        return;
      }

      setLoading(true);
      try {
        const exceptions = await getRuleExceptions(Number(selectedReportingPeriod.reportingPeriodId));
        setRuleExceptions(exceptions);
      } catch (error) {
        console.error("Failed to load rule exceptions:", error);
        setRuleExceptions([]);
      } finally {
        setLoading(false);
      }
    };

    loadRuleExceptions();
  }, [selectedReportingPeriod]);

  const selectedRuleException = useMemo(() => {
    return ruleExceptions.find((e) => e.ruleExceptionId === selectedRuleExceptionId) || null;
  }, [ruleExceptions, selectedRuleExceptionId]);

  const filteredRuleExceptions = useMemo(() => {
    return ruleExceptions.filter((exception) => {
      // Filter by status
      if (filters.status && exception.status !== filters.status) {
        return false;
      }

      // Filter by assigned user
      if (filters.assignedTo && exception.assignedTo !== filters.assignedTo) {
        return false;
      }

      // Filter by search term
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        return (
          exception.shortDescription.toLowerCase().includes(searchLower) ||
          exception.description.toLowerCase().includes(searchLower) ||
          exception.ruleKey.toLowerCase().includes(searchLower)
        );
      }

      return true;
    });
  }, [ruleExceptions, filters]);

  const updateRuleExceptionStatus = useCallback(
    async (ruleExceptionId: number, status: string) => {
      const exception = ruleExceptions.find(e => e.ruleExceptionId === ruleExceptionId);
      if (!exception) return false;

      const updatedException: RuleException = {
        ...exception,
        status,
        updatedAt: new Date().toISOString(),
      };

      try {
        const success = await createRuleException(updatedException);
        if (success) {
          setRuleExceptions((prev) =>
            prev.map((e) =>
              e.ruleExceptionId === ruleExceptionId ? updatedException : e
            )
          );
          return true;
        }
      } catch (error) {
        console.error("Failed to update rule exception status:", error);
      }
      return false;
    },
    [ruleExceptions]
  );

  const assignRuleException = useCallback(
    async (ruleExceptionId: number, assignedTo: string) => {
      const exception = ruleExceptions.find(e => e.ruleExceptionId === ruleExceptionId);
      if (!exception) return false;

      const updatedException: RuleException = {
        ...exception,
        assignedTo,
        updatedAt: new Date().toISOString(),
      };

      try {
        const success = await createRuleException(updatedException);
        if (success) {
          setRuleExceptions((prev) =>
            prev.map((e) =>
              e.ruleExceptionId === ruleExceptionId ? updatedException : e
            )
          );
          return true;
        }
      } catch (error) {
        console.error("Failed to assign rule exception:", error);
      }
      return false;
    },
    [ruleExceptions]
  );

  const updateRuleExceptionNotes = useCallback(
    async (ruleExceptionId: number, notes: string) => {
      const exception = ruleExceptions.find(e => e.ruleExceptionId === ruleExceptionId);
      if (!exception) return false;

      const updatedException: RuleException = {
        ...exception,
        notes,
        updatedAt: new Date().toISOString(),
      };

      try {
        const success = await createRuleException(updatedException);
        if (success) {
          setRuleExceptions((prev) =>
            prev.map((e) =>
              e.ruleExceptionId === ruleExceptionId ? updatedException : e
            )
          );
          return true;
        }
      } catch (error) {
        console.error("Failed to update rule exception notes:", error);
      }
      return false;
    },
    [ruleExceptions]
  );

  const handleFilterChange = useCallback(
    (newFilters: Partial<RuleExceptionFilter>) => {
      setFilters((prev) => ({
        ...prev,
        ...newFilters,
      }));
    },
    []
  );

  return {
    ruleExceptions: filteredRuleExceptions,
    selectedRuleException,
    filters,
    loading,
    selectedReportingPeriod,
    setSelectedRuleExceptionId,
    updateRuleExceptionStatus,
    assignRuleException,
    updateRuleExceptionNotes,
    handleFilterChange,
  };
};
